import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const CookiePolicy = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-gray-100 dark:bg-slate-900 py-12 px-4">
      <div className="container mx-auto max-w-4xl">
        <button onClick={() => navigate('/')} className="flex items-center text-blue-600 hover:text-blue-700 mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
        </button>
        <Card>
          <CardHeader>
            <CardTitle className="text-3xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Cookie Policy</CardTitle>
            <p className="text-slate-600 dark:text-slate-400 text-sm mt-2">Last Updated: January 26, 2025</p>
          </CardHeader>
          <CardContent className="prose dark:prose-invert max-w-none">
            <p className="text-slate-700 dark:text-slate-300">
              This Cookie Policy explains how SmartPDF Tools uses cookies and similar technologies when you use our service.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">1. What Are Cookies?</h2>
            <p className="text-slate-700 dark:text-slate-300">
              Cookies are small text files stored on your device when you visit websites. They help websites remember your preferences and improve your experience.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">2. Types of Cookies We Use</h2>
            
            <h3 className="text-xl font-semibold mt-6 mb-3">2.1 Essential Cookies (Required)</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300">
              <li><strong>Authentication:</strong> Keep you logged in securely</li>
              <li><strong>Session Management:</strong> Maintain your session state</li>
              <li><strong>Security:</strong> Prevent fraud and protect your account</li>
              <li><strong>Duration:</strong> Session cookies (deleted when browser closes) or 24 hours</li>
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-3">2.2 Preference Cookies</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300">
              <li><strong>Theme Settings:</strong> Remember your dark/light mode choice</li>
              <li><strong>Language:</strong> Save language preferences</li>
              <li><strong>Duration:</strong> Persistent (1 year)</li>
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-3">2.3 Analytics Cookies</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300">
              <li><strong>Usage Tracking:</strong> Understand how you use our service</li>
              <li><strong>Performance:</strong> Monitor service speed and errors</li>
              <li><strong>Anonymized Data:</strong> Cannot personally identify you</li>
              <li><strong>Duration:</strong> Up to 2 years</li>
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-3">2.4 Third-Party Cookies</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300">
              <li><strong>Stripe:</strong> Payment processing security</li>
              <li><strong>Google AdSense:</strong> Ad delivery (free users only)</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">3. Cookie Details</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Cookie Name</th>
                    <th className="text-left py-2">Type</th>
                    <th className="text-left py-2">Purpose</th>
                    <th className="text-left py-2">Duration</th>
                  </tr>
                </thead>
                <tbody className="text-slate-700 dark:text-slate-300">
                  <tr className="border-b">
                    <td className="py-2">auth_token</td>
                    <td className="py-2">Essential</td>
                    <td className="py-2">User authentication</td>
                    <td className="py-2">24 hours</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2">theme</td>
                    <td className="py-2">Preference</td>
                    <td className="py-2">Dark/light mode</td>
                    <td className="py-2">1 year</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2">session_id</td>
                    <td className="py-2">Essential</td>
                    <td className="py-2">Session tracking</td>
                    <td className="py-2">Session</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4">4. How to Control Cookies</h2>
            
            <h3 className="text-xl font-semibold mt-6 mb-3">4.1 Browser Settings</h3>
            <p className="text-slate-700 dark:text-slate-300">
              Most browsers allow you to control cookies through settings:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300 mt-3">
              <li><strong>Chrome:</strong> Settings → Privacy and Security → Cookies</li>
              <li><strong>Firefox:</strong> Options → Privacy & Security → Cookies</li>
              <li><strong>Safari:</strong> Preferences → Privacy → Cookies</li>
              <li><strong>Edge:</strong> Settings → Privacy → Cookies</li>
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-3">4.2 Disable Cookies</h3>
            <p className="text-slate-700 dark:text-slate-300">
              ⚠️ <strong>Warning:</strong> Disabling essential cookies will prevent you from logging in and using our service.
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300 mt-3">
              <li><strong>Essential Cookies:</strong> Cannot be disabled (required for service)</li>
              <li><strong>Analytics:</strong> Can opt-out via browser settings</li>
              <li><strong>Ads:</strong> Can use ad blockers or browser privacy settings</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">5. Do Not Track (DNT)</h2>
            <p className="text-slate-700 dark:text-slate-300">
              We respect Do Not Track signals. When DNT is enabled, we will not track your activity beyond essential functionality.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">6. Updates to This Policy</h2>
            <p className="text-slate-700 dark:text-slate-300">
              We may update this Cookie Policy. Changes will be posted on this page with an updated date.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">7. Contact Us</h2>
            <p className="text-slate-700 dark:text-slate-300">
              Questions about cookies? Contact us at: <a href="mailto:support@smrtpdftools.com" className="text-blue-600 hover:text-blue-700">support@smrtpdftools.com</a>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CookiePolicy;